<template>
    <div :class="containerClass">
        <Dialog
            v-model:visible="dialogVisible"
            :header="header"
            :position="position"
            :draggable="draggable"
            :resizable="resizable"
            :maximizable="maximizable"
            :modal="modal"
            :closeOnEscape="closeOnEscape"
            :dismissableMask="dismissableMask"
            :style="style"
            :breakpoints="breakpoints"
            :closable="closable"
            @show="onShow"
            @hide="onHide"
            @maximize="onMaximize"
        >
            <template #header v-if="$slots.header">
                <slot name="header"></slot>
            </template>

            <slot></slot>

            <template #footer v-if="$slots.footer">
                <slot name="footer"></slot>
            </template>
        </Dialog>
    </div>
</template>

<script lang="ts">
import { defineComponent, computed, watch } from 'vue'
import type { PropType } from 'vue'
import Dialog from 'primevue/dialog'
import type { DialogPosition, MOPDialogProps } from './types'

export default defineComponent({
    name: 'MOPDialog',
    components: {
        Dialog
    },
    props: {
        modelValue: {
            type: Boolean as PropType<MOPDialogProps['modelValue']>,
            default: false
        },
        header: {
            type: String as PropType<MOPDialogProps['header']>,
            default: undefined
        },
        position: {
            type: String as PropType<DialogPosition>,
            default: undefined
        },
        draggable: {
            type: Boolean as PropType<MOPDialogProps['draggable']>,
            default: false
        },
        resizable: {
            type: Boolean as PropType<MOPDialogProps['resizable']>,
            default: false
        },
        maximizable: {
            type: Boolean as PropType<MOPDialogProps['maximizable']>,
            default: false
        },
        modal: {
            type: Boolean as PropType<MOPDialogProps['modal']>,
            default: true
        },
        closeOnEscape: {
            type: Boolean as PropType<MOPDialogProps['closeOnEscape']>,
            default: true
        },
        dismissableMask: {
            type: Boolean as PropType<MOPDialogProps['dismissableMask']>,
            default: false
        },
        rtl: {
            type: Boolean as PropType<MOPDialogProps['rtl']>,
            default: false
        },
        closable:{
            type: Boolean as PropType<MOPDialogProps['closable']>,
                default: true
        },
        style: {
            type: Object as PropType<MOPDialogProps['style']>,
            default: () => ({})
        },
        className: {
            type: String as PropType<MOPDialogProps['className']>,
            default: ''
        },
        breakpoints: {
            type: Object as PropType<MOPDialogProps['breakpoints']>,
            default: () => ({})
        }
    },
    emits: {
        'update:modelValue': (value: boolean) => true,
        'show': () => true,
        'hide': () => true,
        'maximize': (event: Event) => true
    },
    setup(props, { emit }) {
        const dialogVisible = computed({
            get: () => props.modelValue,
            set: (value: boolean) => {
                emit('update:modelValue', value)
            }
        })

        const containerClass = computed(() => {
            return [
                'mop-dialog',
                props.className,
                {
                    'mop-dialog--rtl': props.rtl
                }
            ]
        })

        watch(() => props.modelValue, (newValue) => {
            if (newValue) {
                emit('show')
            } else {
                emit('hide')
            }
        }, { immediate: true })

        const onShow = () => {
            emit('show')
        }

        const onHide = () => {
            emit('hide')
        }

        const onMaximize = (event: Event) => {
            emit('maximize', event)
        }

        return {
            dialogVisible,
            containerClass,
            onShow,
            onHide,
            onMaximize
        }
    }
})
</script>

<style lang="scss">
.mop-dialog {
    &--rtl {
        direction: rtl;
    }
}
</style>
